import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import Icon from '../../../../components/AppIcon';
import { getFullUrl } from '../../../../utils/url';
import { appDate, appWeekday } from '../../../../utils/appDateFormat';

// One visual tone per clocking type. Entering work reads success-tinted, leaving
// reads primary-tinted, breaks read warning-tinted — so a session is scannable
// without reading a single label. All values are design tokens (dark-mode safe).
const CLOCKING_TONE = {
  ClockIn: { icon: 'LogIn', bg: 'bg-success/10', fg: 'text-success', ring: 'ring-success/30' },
  StartTask: { icon: 'PlayCircle', bg: 'bg-success/10', fg: 'text-success', ring: 'ring-success/30' },
  ClockOut: { icon: 'LogOut', bg: 'bg-primary/10', fg: 'text-primary', ring: 'ring-primary/30' },
  StopTask: { icon: 'StopCircle', bg: 'bg-primary/10', fg: 'text-primary', ring: 'ring-primary/30' },
  BreakIn: { icon: 'Coffee', bg: 'bg-warning/10', fg: 'text-warning', ring: 'ring-warning/30' },
  BreakOut: { icon: 'Undo2', bg: 'bg-warning/10', fg: 'text-warning', ring: 'ring-warning/30' },
};

const FALLBACK_TONE = { icon: 'Clock', bg: 'bg-muted', fg: 'text-muted-foreground', ring: 'ring-border' };

const clockingTypeLabel = (type, tr) => {
  switch (type) {
    case 'ClockIn':
      return tr('Clock In');
    case 'ClockOut':
      return tr('Clock Out');
    case 'BreakIn':
      return tr('Break In');
    case 'BreakOut':
      return tr('Break Out');
    case 'StartTask':
      return tr('Start Task');
    case 'StopTask':
      return tr('Stop Task');
    default:
      return type ? String(type) : '';
  }
};

const ClockingRow = ({ entry, isLast }) => {
  const { t } = useTranslation();
  const tone = CLOCKING_TONE[entry?.type] || FALLBACK_TONE;
  const photo = getFullUrl(entry?.imageUrl);

  return (
    <li className="flex gap-3">
      {/* Rail: marker + connector. Flex order flips automatically under RTL. */}
      <div className="flex flex-col items-center flex-shrink-0">
        <span
          className={`grid place-items-center h-7 w-7 rounded-full ring-1 ${tone.bg} ${tone.fg} ${tone.ring}`}
          aria-hidden="true"
        >
          <Icon name={tone.icon} size={13} />
        </span>
        {!isLast && <span className="w-px flex-1 mt-1 bg-border" aria-hidden="true" />}
      </div>

      <div className={`min-w-0 flex-1 ${isLast ? 'pb-0' : 'pb-4'}`}>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
              <span className="text-base font-semibold text-foreground tabular-nums">{entry?.timeLabel}</span>
              <span className="text-xs font-medium text-muted-foreground">
                {clockingTypeLabel(entry?.type, t)}
              </span>
              {entry?.isEdited && (
                <span className="rounded-full bg-warning/10 px-1.5 py-0.5 text-[10px] font-medium text-warning">
                  {t('Edited')}
                </span>
              )}
              {entry?.clientType && (
                <Icon
                  name={entry.clientType === 'Mobile' ? 'Smartphone' : 'Monitor'}
                  size={12}
                  className="text-muted-foreground/70"
                  aria-label={entry.clientType === 'Mobile' ? t('Mobile') : t('Web')}
                />
              )}
            </div>

            <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground line-clamp-2" title={entry?.address || ''}>
              {entry?.address || t('Location not provided')}
            </p>

            {entry?.comments && (
              <p className="mt-1 text-xs text-foreground/80 line-clamp-2" title={entry.comments}>
                {entry.comments}
              </p>
            )}
          </div>

          {photo && (
            <a
              href={photo}
              target="_blank"
              rel="noreferrer"
              title={t('View photo')}
              className="flex-shrink-0 overflow-hidden rounded-md ring-1 ring-border transition-colors hover:ring-primary/50"
            >
              <img src={photo} alt="" className="h-10 w-10 object-cover" loading="lazy" />
            </a>
          )}
        </div>
      </div>
    </li>
  );
};

const SessionBlock = ({ session, index, total }) => {
  const { t } = useTranslation();
  const entries = session?.entries || [];

  return (
    <section>
      <div className="mb-3 flex flex-wrap items-baseline gap-x-2 gap-y-1">
        {total > 1 && (
          <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {t('Session {{n}}', { n: index + 1 })}
          </span>
        )}
        <span className="text-sm font-medium text-foreground tabular-nums">
          {session?.startLabel || '—'}
          {' – '}
          {session?.endLabel || t('In progress')}
        </span>
        {session?.durationLabel && (
          <span className="text-xs text-muted-foreground">· {session.durationLabel}</span>
        )}
      </div>

      <ol className="space-y-0">
        {entries.map((entry, i) => (
          <ClockingRow key={entry.key} entry={entry} isLast={i === entries.length - 1} />
        ))}
      </ol>
    </section>
  );
};

const DetailedDayViewPanel = ({
  selectedDate,
  selectedDateDetails,
  sessions = [],
  getUiStatusBadgeClass,
  getUiStatusLabel,
} = {}) => {
  const { t } = useTranslation();
  const rtl = useSelector((state) => state.user.rtl);
  const titleIconGapClass = rtl ? 'ml-2 mr-0' : 'mr-2 ml-0';

  const hasSessions = Array.isArray(sessions) && sessions.length > 0;

  return (
    <div className="bg-card border border-border rounded-xl">
      <div className="p-6 border-b border-border">
        <h3 className="text-lg font-semibold text-foreground flex items-center">
          <Icon name="Eye" size={18} className={titleIconGapClass} />
          {t('Detailed Day View')}
        </h3>
        <p className="text-sm text-muted-foreground mt-1">
          {t('Click on a calendar date to view detailed attendance')}
        </p>
      </div>
      <div className="p-6">
        {selectedDate && selectedDateDetails ? (
          <div className="space-y-5">
            <div className="flex items-center justify-between gap-3">
              <h4 className="font-medium text-foreground">
                {`${appWeekday(new Date(selectedDate))}, ${appDate(new Date(selectedDate))}`}
              </h4>
              <div
                className={`shrink-0 px-2 py-1 rounded-full text-xs font-medium ${
                  getUiStatusBadgeClass?.(selectedDateDetails?.status)
                }`}
              >
                {getUiStatusLabel?.(selectedDateDetails?.status)}
              </div>
            </div>

            {hasSessions ? (
              <div className="space-y-6">
                {sessions.map((session, idx) => (
                  <SessionBlock
                    key={session.key}
                    session={session}
                    index={idx}
                    total={sessions.length}
                  />
                ))}
              </div>
            ) : (
              <div className="rounded-lg border border-dashed border-border px-4 py-6 text-center">
                <Icon name="Clock" size={28} className="mx-auto mb-2 text-muted-foreground opacity-60" />
                <p className="text-sm text-muted-foreground">{t('No clockings recorded for this day')}</p>
                {selectedDateDetails?.reason && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {t('Reason')}: {selectedDateDetails?.reason}
                  </p>
                )}
              </div>
            )}

            {selectedDateDetails?.hoursWorked > 0 && (
              <div className="pt-3 border-t border-border">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">{t('Total Hours Worked')}</span>
                  <span className="text-lg font-semibold text-foreground tabular-nums">
                    {selectedDateDetails?.hoursWorked} {t('hours')}
                  </span>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            <Icon name="Calendar" size={48} className="mx-auto mb-3 opacity-50" />
            <p>{t('Select a date from the calendar to view details')}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DetailedDayViewPanel;
